import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    HeaderLeft,
    HeaderRight,
    HeaderWrapper,
    MenuIconMobile,
    MobileSearchButton,
} from '@/components/Hypertracker/Header/HeaderStyled';
import { HeaderContent } from '@/pages/Storage/StorageStyled';
import { Divider, IconButton, Typography } from '@mui/material';
import HypertrackerMobileLogo from '@/assets/images/icons/hypertracker_mobile.svg?react';
import MobileDrawerCloseIcon from '@/assets/images/icons/mobile_drawer_close_icon.svg?react';
import { useNavigate } from 'react-router';
import { ROUTES } from '@/lib/constants';
import HyperSearch from '@/components/Hypertracker/HyperSearch';
import {
    setIsAsideMenu,
    setIsHyperSearch,
    setIsTryingToClaimWallet,
} from '@/state/general/generalSlice';
import { resetModalWindow } from '@/state/hypertracker/hypertackerSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { selectIsAsideMenu, selectIsHyperSearchMenu } from '@/state/general/generalSelectors';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import LanguageIcon from '@mui/icons-material/Language';
import LanguageMenu from '@/components/Hypertracker/Header/LanguageMenu/LanguageMenu';

const MobileHeader = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isHyperSearchMenu = useSelector((state: RootState) => selectIsHyperSearchMenu(state));
    const isAsideMenu = useSelector((state: RootState) => selectIsAsideMenu(state));

    const navigateHome = () => {
        navigate(ROUTES.HYPERTRACKER);
    };

    const handleHyperSearchMenuToggle = () => {
        dispatch(setIsHyperSearch(!isHyperSearchMenu));
        dispatch(resetModalWindow());
        dispatch(setIsTryingToClaimWallet(false));
    };

    const handleAsideMenuToggle = () => {
        dispatch(setIsAsideMenu(!isAsideMenu));
        dispatch(resetModalWindow());
        dispatch(setIsTryingToClaimWallet(false));
    };

    const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null);
    const handleLangClick = (event: React.MouseEvent<HTMLElement>) => {
        setLangAnchorEl(event.currentTarget);
    };

    const handleLangClose = () => {
        setLangAnchorEl(null);
    };

    return (
        <HeaderWrapper>
            <HeaderContent
                sx={(theme) => ({
                    [theme.breakpoints.down('md')]: {
                        padding: isAsideMenu ? '8px' : '24px',
                    },
                })}
            >
                <HeaderLeft>
                    <HypertrackerMobileLogo onClick={navigateHome} style={{ cursor: 'pointer' }} />
                </HeaderLeft>
                <HeaderRight>
                    <HyperSearch id={'search-open'} />
                    {isAsideMenu && (
                        <>
                            <MobileSearchButton onClick={handleHyperSearchMenuToggle}>
                                <SearchIcon sx={{ height: '16px', width: 'auto' }} />
                                <Typography
                                    variant={'p12'}
                                    color={'common.white'}
                                    fontWeight={600}
                                    paddingBottom={'2px'}
                                >
                                    {t('hypersearch.title')}
                                </Typography>
                            </MobileSearchButton>
                            <Divider
                                orientation={'vertical'}
                                sx={{
                                    backgroundColor: (theme) => theme.palette.backgrounds.bg3,
                                    height: '18px',
                                    width: '1px',
                                    margin: '0 4px',
                                }}
                            />
                            <IconButton
                                size={'small'}
                                onClick={handleLangClick}
                                data-testid="language-button"
                                aria-label="Language"
                                sx={{
                                    padding: 0,
                                    margin: 0,
                                }}
                            >
                                <LanguageIcon
                                    sx={{
                                        width: '16px',
                                        height: '16px',
                                        color: (theme) => theme.palette.customText.text1,
                                    }}
                                />
                            </IconButton>
                            <Divider
                                orientation={'vertical'}
                                sx={{
                                    backgroundColor: (theme) => theme.palette.backgrounds.bg3,
                                    height: '18px',
                                    width: '1px',
                                }}
                            />
                        </>
                    )}
                    {isHyperSearchMenu ? (
                        <MenuIconMobile onClick={handleHyperSearchMenuToggle}>
                            <MobileDrawerCloseIcon />
                        </MenuIconMobile>
                    ) : (
                        <MenuIconMobile onClick={handleAsideMenuToggle}>
                            {isAsideMenu ? <MobileDrawerCloseIcon /> : <MenuIcon />}
                        </MenuIconMobile>
                    )}
                </HeaderRight>
            </HeaderContent>
            <LanguageMenu
                anchorEl={langAnchorEl}
                handleClose={handleLangClose}
                open={Boolean(langAnchorEl)}
            />
        </HeaderWrapper>
    );
};

export default MobileHeader;
